import { useIsMobile } from '@brunommsilva/react-foundations';
import { SvgIconComponent } from '@mui/icons-material';
import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';

import MinimalLineChart from '../charts/minimal-line-chart';

const COMPACT_CHART_HEIGHT = 48;

interface KpiCardProps {
  title: string;
  /** Already formatted. */
  value: string;
  Icon: SvgIconComponent;
  color: string;
  loading: boolean;
  /** The line under the value: its values, and the label of each one. */
  chartData?: number[];
  labels?: string[];
}

/** On a phone, four cards one under the other are taller than the screen: the card is smaller, two to a row. */
const CompactKpiCard = ({
  title,
  value,
  Icon,
  color,
  loading,
  chartData,
  labels = [],
}: KpiCardProps) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ px: 1.5, py: 1, '&:last-child': { pb: 1 } }}>
      {loading ? (
        <>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="80%" height={28} />
          <Skeleton
            variant="rounded"
            width="100%"
            height={COMPACT_CHART_HEIGHT}
          />
        </>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
            }}
          >
            <Typography
              variant="overline"
              noWrap
              sx={{
                color: 'text.secondary',
                fontWeight: 'bold',
                lineHeight: 1.6,
              }}
            >
              {title}
            </Typography>
            <Icon sx={{ color: color, fontSize: 18 }} />
          </Box>
          <Typography
            variant="h6"
            component="div"
            noWrap
            sx={{ fontWeight: 'bold' }}
          >
            {value}
          </Typography>
          {chartData && (
            <Box sx={{ height: COMPACT_CHART_HEIGHT, width: '100%', mt: 0.5 }}>
              <MinimalLineChart
                labels={labels}
                series={[{ values: chartData, colors: [color] }]}
              />
            </Box>
          )}
        </>
      )}
    </CardContent>
  </Card>
);

const KpiCard = ({
  title,
  value,
  Icon,
  color,
  loading,
  chartData,
  labels = [],
}: KpiCardProps) => {
  const isMobile = useIsMobile();

  return isMobile ? (
    <CompactKpiCard
      title={title}
      value={value}
      Icon={Icon}
      color={color}
      loading={loading}
      chartData={chartData}
      labels={labels}
    />
  ) : (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex' }}>
      <CardContent
        sx={{ p: 2, display: 'flex', width: '100%', '&:last-child': { pb: 2 } }}
      >
        {/* Left Side: Text and Value */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {loading ? (
            <>
              <Skeleton
                variant="text"
                width="60%"
                height={24}
                sx={{ mb: 0.5 }}
              />
              <Skeleton variant="text" width="80%" height={40} />
            </>
          ) : (
            <>
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 'bold',
                  mb: 0.5,
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="h5"
                component="div"
                noWrap
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {value}
              </Typography>
            </>
          )}
        </Box>

        {/* Right Side: Icon and Chart */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            minWidth: '90px',
            width: '45%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              mb: 1,
            }}
          >
            {loading ? (
              <Skeleton variant="circular" width={28} height={28} />
            ) : (
              <Icon sx={{ color: color, fontSize: 28 }} />
            )}
          </Box>
          <Box sx={{ height: 65, width: '100%' }}>
            {loading ? (
              <Skeleton variant="rounded" width="100%" height="100%" />
            ) : chartData ? (
              <MinimalLineChart
                labels={labels}
                series={[{ values: chartData, colors: [color] }]}
              />
            ) : null}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default KpiCard;
